/**
 * AdminZonePicker — selector de zona para la extracción, con dos modos:
 *
 *   - **"Zona geográfica"** (`zone_mode='admin'`): BUSCADOR con autocompletado.
 *     El usuario teclea el nombre (ciudad, provincia o región) y elige el
 *     resultado de una lista jerárquica (p.ej. "Ferrol · Municipio · España").
 *     Sustituye al antiguo cascade de País + 4 botones de nivel que se
 *     desbordaba en el widget estrecho (rediseño UX 2026-06-03). Resultado
 *     full-width, sin botones que se salgan, totalmente responsive.
 *
 *   - **"Por coordenadas"** (`zone_mode='free_circle'`): latitud + longitud +
 *     radio en km. (Antes "Círculo libre"; renombrado porque el usuario
 *     introduce lat/long/radio, no dibuja un círculo.)
 *
 * **CONTRATO preservado**: el store sigue produciendo exactamente lo mismo —
 * en modo zona, `zone_admin.selected_zone` (→ `zone_id` de su `.id`); en modo
 * coordenadas, `zone_circle` (→ `zone_query {center_lat, center_lng,
 * radius_km}`). Las claves internas `zone_mode` ('admin' | 'free_circle') NO
 * cambian: solo cambian las ETIQUETAS visibles.
 *
 * Estilado a juego con el formulario de extracción (Tailwind neutro + acento
 * azul de marca). Resultados INLINE (no flotantes) para no quedar recortados
 * por el `overflow-hidden` del widget movible.
 *
 * @module components/AdminZonePicker
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Globe,
  Crosshair,
  Search,
  Check,
  Loader2,
  MapPin,
  X,
  AlertCircle,
} from 'lucide-react'
import {
  searchAdminZones,
  listAdminZones,
  type AdminLevel,
  type AdminZoneSearchResult,
  ListingsError,
} from '@/services/listingsService'
import { useMapsIntelligentStore } from '@/store/maps_intelligent_store'

interface AdminZonePickerProps {
  disabled?: boolean
}

const LEVEL_LABELS: Record<AdminLevel, string> = {
  country: 'País',
  region: 'Región',
  province: 'Provincia',
  municipality: 'Municipio',
}

function toErrorMessage(e: unknown): string {
  if (e instanceof ListingsError && e.code === 'auth') {
    return 'Tu sesión ha caducado. Inicia sesión de nuevo.'
  }
  return 'No podemos cargar las zonas en este momento.'
}

// ============================================================================
// COMPONENTE PRINCIPAL — toggle de modo + sub-selector
// ============================================================================

export function AdminZonePicker({ disabled = false }: AdminZonePickerProps) {
  const zone_mode = useMapsIntelligentStore(s => s.zone_mode)
  const zone_admin = useMapsIntelligentStore(s => s.zone_admin)
  const zone_circle = useMapsIntelligentStore(s => s.zone_circle)
  const set_zone_mode = useMapsIntelligentStore(s => s.set_zone_mode)
  const set_zone_admin = useMapsIntelligentStore(s => s.set_zone_admin)
  const set_zone_circle = useMapsIntelligentStore(s => s.set_zone_circle)

  return (
    <div className="space-y-3">
      {/* Toggle de modo — grid full-width: nunca se desborda. */}
      <div
        role="tablist"
        aria-label="Tipo de zona"
        className="grid grid-cols-2 gap-1.5"
      >
        <ModeButton
          active={zone_mode === 'admin'}
          disabled={disabled}
          onClick={() => set_zone_mode('admin')}
          icon={<Globe size={14} />}
          label="Zona geográfica"
        />
        <ModeButton
          active={zone_mode === 'free_circle'}
          disabled={disabled}
          onClick={() => set_zone_mode('free_circle')}
          icon={<Crosshair size={14} />}
          label="Por coordenadas"
        />
      </div>

      {zone_mode === 'admin' ? (
        <ZoneSearchSelector
          selected_zone={zone_admin.selected_zone}
          disabled={disabled}
          onSelect={zone =>
            set_zone_admin({ selected_zone: zone, query_text: zone?.name ?? '' })
          }
        />
      ) : (
        <CoordinatesSelector
          center_lat={zone_circle.center_lat}
          center_lng={zone_circle.center_lng}
          radius_km={zone_circle.radius_km}
          disabled={disabled}
          on_change={partial => set_zone_circle(partial)}
        />
      )}
    </div>
  )
}

function ModeButton({
  active,
  disabled,
  onClick,
  icon,
  label,
}: {
  active: boolean
  disabled: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-md border text-xs font-semibold leading-tight text-center transition-colors duration-150 disabled:opacity-50 ${
        active
          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-200'
          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <span className="flex-shrink-0">{icon}</span>
      <span>{label}</span>
    </button>
  )
}

// ============================================================================
// MODO "Zona geográfica" — buscador con autocompletado jerárquico (inline)
// ============================================================================

function ZoneSearchSelector({
  selected_zone,
  disabled,
  onSelect,
}: {
  selected_zone: AdminZoneSearchResult | null
  disabled: boolean
  onSelect: (zone: AdminZoneSearchResult | null) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AdminZoneSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [countryNames, setCountryNames] = useState<Record<string, string>>({})

  // Mapa código país → nombre (una sola carga, para el subtítulo del resultado).
  useEffect(() => {
    const ctrl = new AbortController()
    listAdminZones({ level: 'country', signal: ctrl.signal })
      .then(zones => {
        const map: Record<string, string> = {}
        for (const z of zones) map[z.country_code] = z.name
        setCountryNames(map)
      })
      .catch(() => { /* el subtítulo cae al código de país: no es crítico */ })
    return () => ctrl.abort()
  }, [])

  // Búsqueda fuzzy con debounce 300 ms + min 2 chars (lo valida el backend).
  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      setLoading(false)
      setError(null)
      return
    }
    const ctrl = new AbortController()
    let cancelled = false
    setLoading(true)
    setError(null)
    const t = window.setTimeout(() => {
      searchAdminZones({ q, limit: 20, signal: ctrl.signal })
        .then(items => { if (!cancelled) { setResults(items); setActiveIndex(items.length ? 0 : -1) } })
        .catch(e => {
          if (cancelled || (e instanceof DOMException && e.name === 'AbortError')) return
          setError(toErrorMessage(e))
          setResults([])
        })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 300)
    return () => { cancelled = true; ctrl.abort(); window.clearTimeout(t) }
  }, [query])

  function subtitle(z: AdminZoneSearchResult): string {
    const country = countryNames[z.country_code] ?? z.country_code
    return `${LEVEL_LABELS[z.level]} · ${country}`
  }

  function choose(z: AdminZoneSearchResult) {
    onSelect(z)
    setQuery('')
    setResults([])
    setActiveIndex(-1)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < results.length) choose(results[activeIndex])
    }
  }

  // Zona ya seleccionada → tarjeta de confirmación con opción de cambiarla.
  if (selected_zone) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
        <Check size={15} className="mt-0.5 flex-shrink-0 text-blue-600" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-blue-900">{selected_zone.name}</p>
          <p className="text-[11px] text-blue-700">
            {subtitle(selected_zone)} · {selected_zone.area_km2.toLocaleString('es-ES')} km²
          </p>
        </div>
        <button
          type="button"
          onClick={() => onSelect(null)}
          disabled={disabled}
          className="flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
        >
          <X size={12} /> Cambiar
        </button>
      </div>
    )
  }

  const showHint = query.trim().length > 0 && query.trim().length < 2

  return (
    <div>
      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          role="combobox"
          aria-expanded={results.length > 0}
          aria-autocomplete="list"
          aria-label="Buscar zona por nombre"
          placeholder="Busca ciudad, provincia o región…"
          className="block w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
        />
        {loading && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
        )}
      </div>

      {showHint && (
        <p className="mt-1 px-1 text-[11px] text-gray-500">Escribe al menos 2 letras…</p>
      )}

      {error && (
        <div role="alert" className="mt-1 flex items-center gap-1.5 rounded-md border border-red-100 bg-red-50 px-2 py-1.5 text-xs text-red-700">
          <AlertCircle size={11} className="flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Resultados INLINE (no flotantes) → nunca recortados por el widget. */}
      {results.length > 0 && (
        <ul
          role="listbox"
          aria-label="Resultados de zona"
          className="mt-1.5 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-sm"
        >
          {results.map((z, idx) => (
            <li
              key={z.id}
              role="option"
              aria-selected={idx === activeIndex}
              onMouseDown={e => { e.preventDefault(); choose(z) }}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`flex cursor-pointer items-center gap-2 px-3 py-2 ${
                idx === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <MapPin size={13} className="flex-shrink-0 text-blue-500" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-gray-900">{z.name}</span>
                <span className="block text-[11px] text-gray-500">{subtitle(z)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
        <p className="mt-1.5 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-[11px] text-gray-500">
          Sin coincidencias para «{query.trim()}». Prueba con el nombre de la ciudad o la provincia.
        </p>
      )}
    </div>
  )
}

// ============================================================================
// MODO "Por coordenadas" — latitud + longitud + radio (km)
// ============================================================================

function CoordinatesSelector({
  center_lat,
  center_lng,
  radius_km,
  disabled,
  on_change,
}: {
  center_lat: number
  center_lng: number
  radius_km: number
  disabled: boolean
  on_change: (partial: { center_lat?: number; center_lng?: number; radius_km?: number }) => void
}) {
  const handleLat = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number.parseFloat(e.target.value)
    if (Number.isFinite(v) && v >= -90 && v <= 90) on_change({ center_lat: v })
  }
  const handleLng = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number.parseFloat(e.target.value)
    if (Number.isFinite(v) && v >= -180 && v <= 180) on_change({ center_lng: v })
  }
  const handleRadius = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number.parseInt(e.target.value, 10)
    if (Number.isFinite(v)) on_change({ radius_km: v })
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs leading-relaxed text-gray-600">
        Introduce un punto (latitud y longitud) y un radio de búsqueda en kilómetros.
        Útil para zonas que no encajan en una división administrativa concreta.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">Latitud</span>
          <input
            type="number" step="0.0001" min="-90" max="90"
            value={center_lat} onChange={handleLat} disabled={disabled}
            aria-label="Latitud del punto central"
            className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">Longitud</span>
          <input
            type="number" step="0.0001" min="-180" max="180"
            value={center_lng} onChange={handleLng} disabled={disabled}
            aria-label="Longitud del punto central"
            className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50"
          />
        </label>
      </div>
      <label className="block">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">Radio de búsqueda</span>
          <span className="text-xs font-semibold text-blue-700">{radius_km} km</span>
        </div>
        <input
          type="range" min="5" max="100" step="5"
          value={radius_km} onChange={handleRadius} disabled={disabled}
          aria-label="Radio de búsqueda en kilómetros"
          aria-valuemin={5} aria-valuemax={100} aria-valuenow={radius_km}
          className="w-full accent-blue-500 disabled:opacity-50"
        />
        <div className="mt-1 flex justify-between text-[10px] text-gray-400">
          <span>5 km</span>
          <span>100 km</span>
        </div>
      </label>
    </div>
  )
}
